import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Copy, Check, DollarSign, Users, TrendingUp, Link2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import SelfReferralManager from '../SelfReferralManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralPanel = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchReferralData = useCallback(async () => {
    if (!token) return;
    try {
      const [codeResponse, earningsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/referrals/my-code`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/referrals/content/my-earnings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setReferralData({
        ...codeResponse.data,
        contentEarnings: earningsResponse.data
      });
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load referral data',
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchReferralData();
  }, [fetchReferralData]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast({
      title: 'Copied!',
      description: 'Referral link copied to clipboard',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-400">
        Loading referral data...
      </div>
    );
  }

  if (!referralData) {
    return (
      <div className="text-center py-8 text-gray-400">
        Unable to load referral data
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-full bg-gray-900 mb-6">
          <TabsTrigger
            value="overview"
            className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-purple-500 data-[state=active]:text-black"
          >
            <Gift className="w-4 h-4" />
            Referral Overview
          </TabsTrigger>
          <TabsTrigger
            value="self-referral"
            className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-purple-500 data-[state=active]:text-black"
          >
            <Link2 className="w-4 h-4" />
            Sell Your Content
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-400/10 to-purple-500/10 rounded-lg p-6 border border-yellow-500/30">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                <Gift className="w-6 h-6 text-black" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">
                  Refer Friends & Earn Rewards
                </h3>
                <p className="text-gray-300">
                  Share your unique referral link and earn $5 for every friend who subscribes to the Standard plan!
                </p>
              </div>
            </div>
          </div>

      {/* Total Earnings Summary */}
      <Card className="bg-gradient-to-r from-yellow-400/20 to-purple-500/20 border-2 border-yellow-500/50">
        <CardContent className="p-6">
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-2">Total Earnings (All Sources)</p>
            <div className="text-5xl font-black bg-gradient-to-r from-yellow-400 to-purple-500 bg-clip-text text-transparent">
              ${referralData.contentEarnings?.total_earnings?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              From user referrals + content sharing
            </p>
          </div>
        </CardContent>
      </Card>

      {/* User Referrals Stats */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          User Referrals
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-sm text-gray-400">Total Referrals</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {referralData.total_referrals}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="text-sm text-gray-400">Successful</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {referralData.successful_referrals}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-yellow-400" />
                <span className="text-sm text-gray-400">Earnings</span>
              </div>
              <div className="text-3xl font-bold text-white">
                ${referralData.contentEarnings?.user_referrals?.earnings?.toFixed(2) || '0.00'}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content Referrals Stats */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Gift className="w-5 h-5 text-purple-400" />
          Content Referrals (Share & Earn)
        </h3>
        
        {/* As Referrer (Sharing others' content) */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-6">
            <h4 className="text-md font-semibold text-white mb-4">Earnings from Sharing Content</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-400">Shares</p>
                <p className="text-2xl font-bold text-white">
                  {referralData.contentEarnings?.content_referrals_as_referrer?.total_shares || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Views</p>
                <p className="text-2xl font-bold text-white">
                  {referralData.contentEarnings?.content_referrals_as_referrer?.total_views || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Conversions</p>
                <p className="text-2xl font-bold text-white">
                  {referralData.contentEarnings?.content_referrals_as_referrer?.total_conversions || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Earnings</p>
                <p className="text-2xl font-bold text-yellow-400">
                  ${referralData.contentEarnings?.content_referrals_as_referrer?.earnings?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* As Creator (Others sharing your content) */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-6">
            <h4 className="text-md font-semibold text-white mb-4">Earnings from Your Content Being Shared</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-400">Times Shared</p>
                <p className="text-2xl font-bold text-white">
                  {referralData.contentEarnings?.content_referrals_as_creator?.times_shared || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Views</p>
                <p className="text-2xl font-bold text-white">
                  {referralData.contentEarnings?.content_referrals_as_creator?.total_views || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Conversions</p>
                <p className="text-2xl font-bold text-white">
                  {referralData.contentEarnings?.content_referrals_as_creator?.total_conversions || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Earnings</p>
                <p className="text-2xl font-bold text-yellow-400">
                  ${referralData.contentEarnings?.content_referrals_as_creator?.earnings?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Code */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Your Referral Code</h4>
          <div className="flex items-center gap-3">
            <Badge className="bg-gradient-to-r from-yellow-400 to-purple-500 text-black text-lg px-4 py-2">
              {referralData.referral_code}
            </Badge>
            <Button
              onClick={() => handleCopy(referralData.referral_code)}
              variant="outline"
              className="border-gray-600 hover:bg-gray-700 text-white"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Referral Link */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Your Referral Link</h4>
          <div className="flex items-center gap-3">
            <Input
              value={referralData.referral_link}
              readOnly
              className="bg-gray-900 border-gray-700 text-white flex-1"
            />
            <Button
              onClick={() => handleCopy(referralData.referral_link)}
              className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </>
              )}
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-3">
            Share this link with your friends. When they sign up and subscribe, you both win!
          </p>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <h4 className="text-lg font-semibold text-white mb-4">How It Works</h4>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0 text-black font-bold">
                1
              </div>
              <div>
                <p className="text-white font-medium">Share your link</p>
                <p className="text-sm text-gray-400">Send your referral link to friends who love Black culture and content</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0 text-black font-bold">
                2
              </div>
              <div>
                <p className="text-white font-medium">They subscribe</p>
                <p className="text-sm text-gray-400">Your friend signs up and subscribes to the Standard plan</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0 text-black font-bold">
                3
              </div>
              <div>
                <p className="text-white font-medium">You earn rewards</p>
                <p className="text-sm text-gray-400">Get $5 credited to your account for each successful referral</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="self-referral">
          <SelfReferralManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReferralPanel;
